import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  useTheme,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
  Button,
  IconButton,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  // TextField,
  // InputAdornment,
  // TablePagination,
} from "@mui/material";
import { useGetInvoiceQuery } from "../../api/api-invoices";
import { useGetCurrentUserQuery } from "../../api/api-users-v2";
import { useFormattedAmount } from "../../hooks/useFormattedAmount";
import {
  // PieChart,
  // Pie,
  // Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  XAxis,
  YAxis,
  CartesianGrid,
  AreaChart,
  Area,
  BarChart,
  Bar,
} from "recharts";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
// import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import exportToCsv from "../../helpers/exportToCsv";
import ActiveInvoices from "investor/components/ActiveInvoices";

// Interfaces for TypeScript
interface InvoiceData {
  id?: string;
  principle_amount: number;
  interest_amount: number;
  total_amount: number;
  status?: string;
  sector?: string;
  created_at?: string;
  buyer_name?: string;
  days_financed?: number;
  invoice_id?: string;
  finance_date?: string;
  importer_country?: string;
  exporter_country?: string;
}

interface ApiResponse {
  invoices: InvoiceData[];
  funders: Array<{
    funder_name: string;
    total_principle_amount: number;
  }>;
}

interface MetricsState {
  totalInvoices: number;
  totalPrincipal: number;
  totalInterest: number;
  totalPortfolioValue: number;
  portfolioReturnRate: number;
  cashAvailable: number;
  riskScore: number;
  activeFunders: number;
  invoicesByStatus: Array<{
    name: string;
    count: number;
    value: number;
  }>;
  invoicesBySector: Array<{
    name: string;
    count: number;
    value: number;
  }>;
  importerCountries: Array<{
    name: string;
    count: number;
    value: number;
  }>;
  exporterCountries: Array<{
    name: string;
    count: number;
    value: number;
  }>;
  topInvestors: Array<{
    id: number;
    name: string;
    allocation: number;
    percentage: number;
    returnRate: number;
    riskScore: number;
  }>;
  recentTransactions: Array<{
    id: string;
    date: string;
    type: string;
    buyer: string;
    amount: number;
    days: number;
  }>;
  performanceTrend: Array<{
    month: string;
    aum: number;
    returns: number;
  }>;
  yearlyPerformanceTrend: Array<{
    year: string;
    aum: number;
    returns: number;
  }>;
  alerts: Array<{
    id: number;
    severity: string;
    title: string;
    message: string;
    details: string;
  }>;
}

interface DialogState {
  open: boolean;
  title: string;
  content: string;
}

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    dataKey: string;
    payload: any;
  }>;
  label?: string;
  valueFormatter?: (value: number) => string;
}

interface StatusGroup {
  count: number;
  value: number;
}

interface Investor {
  id: number;
  name: string;
  allocation: number;
  percentage: number;
  returnRate: number;
  riskScore: number;
}

// Styled components for tables
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: "bold",
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
}));

// Use theme as a prop properly
const AlternatingRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== "index",
})<{ index?: number }>(({ theme, index = 0 }) => ({
  backgroundColor: index % 2 === 0 ? theme.palette.action.hover : "transparent",
  "&:hover": {
    backgroundColor: theme.palette.action.selected,
  },
}));

// Custom Tooltip component for charts
const CustomTooltip = ({
  active,
  payload,
  label,
  valueFormatter,
}: TooltipProps) => {
  if (active && payload && payload.length) {
    const value = payload[0]?.value;
    return (
      <Paper
        elevation={3}
        sx={{ p: 2, backgroundColor: "rgba(255, 255, 255, 0.9)" }}
      >
        <Typography variant="subtitle2" color="primary">
          {label}
        </Typography>
        <Typography variant="body2">
          {payload[0].name}:{" "}
          {valueFormatter && typeof value === "number"
            ? valueFormatter(value)
            : value}
        </Typography>
      </Paper>
    );
  }
  return null;
};

// Custom Risk Alert Card component
const RiskAlertCard = ({ alert }: { alert: MetricsState["alerts"][0] }) => {
  const getIcon = () => {
    switch (alert.severity) {
      case "error":
        return <ErrorIcon color="error" fontSize="large" />;
      case "warning":
        return <WarningIcon color="warning" fontSize="large" />;
      case "success":
        return <CheckCircleIcon color="success" fontSize="large" />;
      case "info":
        return <InfoIcon color="info" fontSize="large" />;
      default:
        return null;
    }
  };

  return (
    <Card
      elevation={3}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor:
          alert.severity === "error"
            ? "#fdeded"
            : alert.severity === "warning"
              ? "#fff4e5"
              : alert.severity === "success"
                ? "#edf7ed"
                : "#e5f6fd",
      }}
    >
      <CardContent
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Box display="flex" alignItems="center" mb={1}>
          {getIcon()}
        </Box>
        <Typography variant="h6" align="center" gutterBottom fontWeight="bold">
          {alert.title}
        </Typography>
        <Typography variant="body1" align="center">
          {alert.message}
        </Typography>
      </CardContent>
    </Card>
  );
};

const MasterInvestorDashboard: React.FC = () => {
  const theme = useTheme();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [detailsDialog, setDetailsDialog] = useState<DialogState>({
    open: false,
    content: "",
    title: "",
  });
  const [transactionPage, setTransactionPage] = useState(0);
  const [transactionsPerPage, setTransactionsPerPage] = useState(10);
  const [transactionSearchQuery, setTransactionSearchQuery] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [invoiceChartOpen, setInvoiceChartOpen] = useState(false);

  // Get current user data
  const { data: currentUser, isLoading: isUserLoading } =
    useGetCurrentUserQuery();

  // Extract id from current user
  const id = currentUser?.id;

  // API data fetching - use dynamic id instead of hardcoded value
  // Split API calls: one for summary cards (year=2025), one for all other data
  const {
    data: summaryData,
    isLoading: isSummaryLoading,
    isError: isSummaryError,
    refetch: refetchSummary,
  } = useGetInvoiceQuery(
    {
      endpoint: `dashboard/investors-summary?year=2025`,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !id,
    }
  );

  const {
    data: dashboardData,
    isLoading: isDashboardLoading,
    isError: isDashboardError,
    refetch: refetchDashboard,
  } = useGetInvoiceQuery(
    {
      endpoint: `dashboard/investors-summary`,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !id,
    }
  );

  const { data: historicalData } = useGetInvoiceQuery(
    {
      endpoint: `dashboard/investors-summary`,
    },
    {
      skip: !id,
    }
  );

  const { format2Decimals, formatMoney, formatBalances, formatPercentage } =
    useFormattedAmount();

  // State for summary metrics (top 4 cards)
  const [summaryMetrics, setSummaryMetrics] = useState({
    totalPortfolioValue: 0,
    totalInvoices: 0,
    activeFunders: 0,
    portfolioReturnRate: 0,
  });

  // State for dashboard metrics (all other data)
  const [metrics, setMetrics] = useState<MetricsState>({
    totalInvoices: 0,
    totalPrincipal: 0,
    totalInterest: 0,
    totalPortfolioValue: 0,
    portfolioReturnRate: 0,
    cashAvailable: 0,
    riskScore: 0,
    activeFunders: 0,
    invoicesByStatus: [],
    invoicesBySector: [],
    importerCountries: [],
    exporterCountries: [],
    topInvestors: [],
    recentTransactions: [],
    performanceTrend: [],
    yearlyPerformanceTrend: [],
    alerts: [
      {
        id: 1,
        severity: "warning",
        title: "Approaching Maturity",
        message: "Three invoices are approaching maturity dates within 5 days",
        details: "Invoice IDs: INV-2023-001, INV-2023-005, INV-2023-008",
      },
      {
        id: 2,
        severity: "error",
        title: "Overdue Invoice",
        message: "One invoice is overdue by more than 15 days",
        details: "Invoice ID: INV-2023-002 is overdue by 17 days",
      },
      {
        id: 3,
        severity: "success",
        title: "Onboarding Completed",
        message: "New investor onboarding completed",
        details:
          'Investor "Global Capital Partners" has completed the onboarding process',
      },
    ],
  });

  // Extract master investor name (funder_name) from summaryData.funders
  let masterFunderName = "";
  if (
    summaryData &&
    Array.isArray(summaryData.funders) &&
    summaryData.funders.length > 0
  ) {
    masterFunderName = summaryData.funders[0].funder_name;
  }

  // Add useEffect to trigger a refetch when component mounts
  useEffect(() => {
    refetchSummary();
  }, [refetchSummary]);

  // Process data when it's received from API
  useEffect(() => {
    if (!dashboardData) return;
    
    const invoices = dashboardData.invoices || [];
    const funders = dashboardData.funders || [];
    
    // Filter out pre-listing invoices to ensure consistent calculation
    const activeInvoices = invoices.filter((inv: InvoiceData) => {
      return inv.status !== "pre_listing" &&
             inv.status !== "prelisting" &&
             (inv as any).phase !== "0_PRE_LISTING" && 
             (inv as any).phase !== "PRE_LISTING";
    });

    const totalPortfolioValue = activeInvoices.reduce(
      (sum: number, inv: InvoiceData) =>
        sum + (inv.principle_amount || 0) + (inv.interest_amount || 0),
      0
    );
    const totalInvoices = activeInvoices.length;
    const activeFunders = funders.length;
    
    // Calculate portfolio return rate (annualized)
    const totalPrincipal = activeInvoices.reduce(
      (sum: number, inv: InvoiceData) => sum + (inv.principle_amount || 0),
      0
    );
    const totalInterest = activeInvoices.reduce(
      (sum: number, inv: InvoiceData) => sum + (inv.interest_amount || 0),
      0
    );
    const daysFinanced = activeInvoices.reduce(
      (sum: number, inv: InvoiceData) => sum + (inv.days_financed || 0),
      0
    );
    const portfolioReturnRate =
      totalPrincipal > 0 && daysFinanced > 0
        ? (totalInterest / totalPrincipal) * (365 / daysFinanced) * 100
        : 0;
    setSummaryMetrics({
      totalPortfolioValue,
      totalInvoices,
      activeFunders,
      portfolioReturnRate,
    });
  }, [dashboardData]);

  useEffect(() => {
    if (!dashboardData) return;
    // Get the total cash available from the API response
    const totalCashAvailable = dashboardData?.total_cash_available || 0;
    const allInvoices = dashboardData?.invoices || [];
    const funders = dashboardData?.funders || [];

    // Filter out pre-listing invoices to ensure consistent calculation
    const invoices = allInvoices.filter((inv: InvoiceData) => {
      return inv.status !== "pre_listing" &&
             inv.status !== "prelisting" &&
             (inv as any).phase !== "0_PRE_LISTING" && 
             (inv as any).phase !== "PRE_LISTING";
    });

    // Filter invoices for FY 2025 only (assuming fiscal year 2025 spans from July 2024 to June 2025)
    const fy2025Invoices = invoices.filter((inv: InvoiceData) => {
      if (!inv.finance_date) return false;
      const financeDate = new Date(inv.finance_date);
      return (
        (financeDate.getFullYear() === 2024 && financeDate.getMonth() >= 6) ||
        (financeDate.getFullYear() === 2025 && financeDate.getMonth() <= 5)
      );
    });

    // 1. Active funders will be funders.length
    const activeFunders = funders.length;

    // 2. Active Invoices will be invoices.length
    const activeInvoices = invoices.length;
    const fy2025ActiveInvoices = fy2025Invoices.length;

    // Calculate totals
    const totalPrincipal = invoices.reduce(
      (sum: number, inv: InvoiceData) => sum + (inv.principle_amount || 0),
      0
    );
    const totalInterest = invoices.reduce(
      (sum: number, inv: InvoiceData) => sum + (inv.interest_amount || 0),
      0
    );
    const daysFinanced = invoices.reduce(
      (sum: number, inv: InvoiceData) => sum + (inv.days_financed || 0),
      0
    );

    // Calculate FY 2025 totals
    const fy2025TotalPrincipal = fy2025Invoices.reduce(
      (sum: number, inv: InvoiceData) => sum + (inv.principle_amount || 0),
      0
    );
    const fy2025TotalInterest = fy2025Invoices.reduce(
      (sum: number, inv: InvoiceData) => sum + (inv.interest_amount || 0),
      0
    );
    const fy2025DaysFinanced = fy2025Invoices.reduce(
      (sum: number, inv: InvoiceData) => sum + (inv.days_financed || 0),
      0
    );

    const averageDaysFinanced =
      activeInvoices > 0 ? daysFinanced / activeInvoices : 0;
    const totalPortfolioValue = totalPrincipal + totalInterest;

    // Calculate portfolio return rate - Annualized
    const portfolioReturnRate =
      totalPrincipal > 0 && daysFinanced > 0
        ? (totalInterest / totalPrincipal) * (365 / daysFinanced) * 100
        : 0;

    // Calculate FY 2025 portfolio return rate - Annualized - Use this as our primary return rate (3.31%)
    const fy2025PortfolioReturnRate =
      fy2025TotalPrincipal > 0 && fy2025DaysFinanced > 0
        ? (fy2025TotalInterest / fy2025TotalPrincipal) *
          (365 / fy2025DaysFinanced) *
          100
        : 0;

    const riskScore = 2; // Default risk score

    // Group invoices by status and sector for charts
    const invoicesByStatus = invoices.reduce(
      (acc: Record<string, StatusGroup>, inv: InvoiceData) => {
        const status = inv.status || "Unknown";
        if (!acc[status]) {
          acc[status] = { count: 0, value: 0 };
        }
        acc[status].count += 1;
        acc[status].value += inv.principle_amount || 0;
        return acc;
      },
      {}
    );

    const invoicesBySector = invoices.reduce(
      (acc: Record<string, StatusGroup>, inv: InvoiceData) => {
        const sector = inv.sector || "Unknown";
        if (!acc[sector]) {
          acc[sector] = { count: 0, value: 0 };
        }
        acc[sector].count += 1;
        acc[sector].value += inv.principle_amount || 0;
        return acc;
      },
      {}
    );

    // Group by importer and exporter country for pie charts
    const importerCountries = invoices.reduce(
      (acc: Record<string, StatusGroup>, inv: InvoiceData) => {
        const country = inv.importer_country || "Unknown";
        if (!acc[country]) {
          acc[country] = { count: 0, value: 0 };
        }
        acc[country].count += 1;
        acc[country].value += inv.principle_amount || 0;
        return acc;
      },
      {}
    );

    const exporterCountries = invoices.reduce(
      (acc: Record<string, StatusGroup>, inv: InvoiceData) => {
        const country = inv.exporter_country || "Unknown";
        if (!acc[country]) {
          acc[country] = { count: 0, value: 0 };
        }
        acc[country].count += 1;
        acc[country].value += inv.principle_amount || 0;
        return acc;
      },
      {}
    );

    // Format groups for chart display
    const formattedInvoicesByStatus = Object.entries(invoicesByStatus).map(
      (entry) => {
        const [name, data] = entry as [string, StatusGroup];
        return {
          name,
          count: data.count,
          value: data.value,
        };
      }
    );

    const formattedInvoicesBySector = Object.entries(invoicesBySector).map(
      (entry) => {
        const [name, data] = entry as [string, StatusGroup];
        return {
          name,
          count: data.count,
          value: data.value,
        };
      }
    );

    const formattedImporterCountries = Object.entries(importerCountries).map(
      (entry) => {
        const [name, data] = entry as [string, StatusGroup];
        return {
          name,
          count: data.count,
          value: data.value,
        };
      }
    );

    const formattedExporterCountries = Object.entries(exporterCountries).map(
      (entry) => {
        const [name, data] = entry as [string, StatusGroup];
        return {
          name,
          count: data.count,
          value: data.value,
        };
      }
    );

    // Create investor allocation from funders data
    const topInvestors = funders.map(
      (
        funder: { funder_name: string; total_principle_amount: number },
        index: number
      ) => {
        const percentage =
          totalPortfolioValue > 0
            ? (funder.total_principle_amount / totalPortfolioValue) * 100
            : 0;

        return {
          id: index + 1,
          name: funder.funder_name,
          allocation: funder.total_principle_amount,
          percentage,
          returnRate: 0,
          riskScore: 0,
        };
      }
    );

    // Create recent transactions from invoice data
    const recentTransactions = invoices.map((inv: InvoiceData) => ({
      id: inv.invoice_id || "unknown",
      date: inv.finance_date || "N/A",
      type: "Invoice",
      buyer: inv.buyer_name || "Unknown",
      amount: inv.principle_amount || 0,
      days: inv.days_financed || 0,
    }));

    // Process historical data for the yearly performance chart
    let yearlyPerformanceTrend: Array<{
      year: string;
      aum: number;
      returns: number;
    }> = [];

    if (
      historicalData &&
      historicalData.invoices &&
      historicalData.invoices.length > 0
    ) {
      // Group historical invoices by year
      const historicalInvoicesByYear: Record<
        string,
        {
          principal: number;
          interest: number;
          count: number;
          totalDays: number;
        }
      > = {};

      // Process historical invoices and group by year
      historicalData.invoices.forEach((inv: InvoiceData) => {
        if (!inv.finance_date) return;

        const financeDate = new Date(inv.finance_date);
        const year = financeDate.getFullYear().toString();

        if (!historicalInvoicesByYear[year]) {
          historicalInvoicesByYear[year] = {
            principal: 0,
            interest: 0,
            count: 0,
            totalDays: 0,
          };
        }

        historicalInvoicesByYear[year].principal += inv.principle_amount || 0;
        historicalInvoicesByYear[year].interest += inv.interest_amount || 0;
        historicalInvoicesByYear[year].count += 1;
        historicalInvoicesByYear[year].totalDays += inv.days_financed || 0;
      });

      // Convert to array and calculate return rates
      yearlyPerformanceTrend = Object.entries(historicalInvoicesByYear)
        .map(([year, data]) => {
          // Use the same annualization formula for historical data
          const annualizedReturnRate =
            data.principal > 0 && data.totalDays > 0
              ? (data.interest / data.principal) * (365 / data.totalDays) * 100
              : 0;
          return {
            year,
            aum: data.principal + data.interest,
            returns: annualizedReturnRate,
          };
        })
        .sort((a, b) => parseInt(a.year, 10) - parseInt(b.year, 10));

      // Ensure there's data for years 2021-2025
      const yearsToInclude = ["2021", "2022", "2023", "2024", "2025"];
      const yearsInData = new Set(
        yearlyPerformanceTrend.map((item) => item.year)
      );

      // Fill in missing years
      yearsToInclude.forEach((year) => {
        if (!yearsInData.has(year)) {
          // Generate placeholder data for missing years
          yearlyPerformanceTrend.push({
            year,
            aum: year === "2025" ? totalPortfolioValue : 0,
            returns: year === "2025" ? fy2025PortfolioReturnRate : 0,
          });
        }
      });

      // Sort again after adding missing years
      yearlyPerformanceTrend.sort(
        (a, b) => parseInt(a.year, 10) - parseInt(b.year, 10)
      );
    } else {
      // If no historical data, show empty chart
      yearlyPerformanceTrend = [];
    }

    // IMPORTANT: Ensure the 2025 values match the current portfolio value and return rate
    const year2025Data = yearlyPerformanceTrend.find(
      (item) => item.year === "2025"
    );
    if (year2025Data) {
      year2025Data.aum = totalPortfolioValue;
      year2025Data.returns = summaryMetrics.portfolioReturnRate;
    } else {
      yearlyPerformanceTrend.push({
        year: "2025",
        aum: totalPortfolioValue,
        returns: summaryMetrics.portfolioReturnRate,
      });
      // Sort again
      yearlyPerformanceTrend.sort(
        (a, b) => parseInt(a.year, 10) - parseInt(b.year, 10)
      );
    }

    // Generate monthly data for current year - Keep this for backward compatibility if needed
    const currentMonth = new Date().getMonth();
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const performanceTrend = Array.from(
      { length: currentMonth + 1 },
      (_, i) => {
        // Use a more realistic growth model that ensures the latest month matches total portfolio value
        const monthProgress = i / Math.max(currentMonth, 1); // Normalized progress through the year

        // If it's the current month, use actual total portfolio value
        const aumValue =
          i === currentMonth
            ? totalPortfolioValue
            : totalPortfolioValue * (0.85 + monthProgress * 0.15); // Gradually increase to current value

        // Keep return rate consistent with the 3.31% shown in the UI
        const monthlyReturn = fy2025PortfolioReturnRate;

        return {
          month: months[i],
          aum: aumValue,
          returns: monthlyReturn,
        };
      }
    );

    // Update metrics with the calculated values
    setMetrics({
      totalInvoices: fy2025ActiveInvoices, // Use FY 2025 invoice count
      totalPrincipal,
      totalInterest,
      totalPortfolioValue,
      portfolioReturnRate: fy2025PortfolioReturnRate, // Use FY 2025 specific rate
      cashAvailable: totalCashAvailable,
      riskScore,
      activeFunders,
      invoicesByStatus: formattedInvoicesByStatus,
      invoicesBySector: formattedInvoicesBySector,
      importerCountries: formattedImporterCountries
        .filter((country) => (country.value / totalPortfolioValue) * 100 <= 10)
        .slice(0, 10),
      exporterCountries: formattedExporterCountries
        .filter((country) => (country.value / totalPortfolioValue) * 100 <= 10)
        .slice(0, 10),
      topInvestors: topInvestors,
      recentTransactions,
      performanceTrend,
      yearlyPerformanceTrend,
      alerts: metrics.alerts,
    });
  }, [dashboardData, historicalData]);

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
    refetchSummary();
  };

  const showDetailsDialog = (title: string, content: string) => {
    setDetailsDialog({
      open: true,
      title,
      content,
    });
  };

  const closeDetailsDialog = () => {
    setDetailsDialog({
      ...detailsDialog,
      open: false,
    });
  };

  const formatCurrency = (amount: number): string => {
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return formatter.format(amount);
  };

  // Transaction table pagination handlers
  const handleTransactionChangePage = (event: unknown, newPage: number) => {
    setTransactionPage(newPage);
  };

  const handleTransactionChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTransactionsPerPage(parseInt(event.target.value, 10));
    setTransactionPage(0);
  };

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];
  const formatCurrencylarge = (value: number) => `$${value.toLocaleString()}`;
  const formattedData = [
    metrics.topInvestors.reduce((acc: Record<string, number>, investor) => {
      acc[investor.name] = investor.allocation;
      return acc;
    }, {}),
  ];
  // Filter transactions based on search query
  const filteredTransactions = metrics.recentTransactions.filter((tx: any) => {
    const searchStr = transactionSearchQuery.toLowerCase();
    return (
      tx.id.toLowerCase().includes(searchStr) ||
      tx.buyer.toLowerCase().includes(searchStr) ||
      tx.type.toLowerCase().includes(searchStr) ||
      new Date(tx.date).toLocaleDateString().toLowerCase().includes(searchStr)
    );
  });

  // Paginated transactions
  const paginatedTransactions = filteredTransactions.slice(
    transactionPage * transactionsPerPage,
    transactionPage * transactionsPerPage + transactionsPerPage
  );

  // Handler for invoice click
  const handleInvoiceClick = (invoice: any) => {
    setSelectedInvoice(invoice);
    setInvoiceChartOpen(true);
  };

  // Generate chart data for selected invoice
  const generateInvoiceChartData = () => {
    if (!selectedInvoice) return [];

    // Generate payment schedule based on finance date, due date and amounts
    const financeDate = new Date(selectedInvoice.financeDate);
    const dueDate = new Date(selectedInvoice.dueDate);
    const daysTotal = Math.floor(
      (dueDate.getTime() - financeDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    // Create payment schedule with daily interest accrual
    const dailyInterest = selectedInvoice.interestAmount / daysTotal;

    const chartData = [];
    let accruedInterest = 0;

    // Add starting point
    chartData.push({
      day: 0,
      date: financeDate.toLocaleDateString(),
      principal: selectedInvoice.principalAmount,
      interest: 0,
      total: selectedInvoice.principalAmount,
    });

    // Add data points for every 7 days
    for (let day = 7; day < daysTotal; day += 7) {
      accruedInterest = dailyInterest * day;
      const currentDate = new Date(financeDate);
      currentDate.setDate(financeDate.getDate() + day);

      chartData.push({
        day,
        date: currentDate.toLocaleDateString(),
        principal: selectedInvoice.principalAmount,
        interest: accruedInterest,
        total: selectedInvoice.principalAmount + accruedInterest,
      });
    }

    // Add end point
    chartData.push({
      day: daysTotal,
      date: dueDate.toLocaleDateString(),
      principal: selectedInvoice.principalAmount,
      interest: selectedInvoice.interestAmount,
      total: selectedInvoice.totalAmount,
    });

    return chartData;
  };

  // Show loading state for summary cards
  if (isSummaryLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading dashboard data...
        </Typography>
      </Box>
    );
  }

  // Show error state for summary cards
  if (isSummaryError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <AlertTitle>Error loading dashboard</AlertTitle>
          There was a problem loading the dashboard data. Please try refreshing
          the page.
        </Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={handleRefresh}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: theme.palette.background.default }}>
      {/* Welcome message */}
      {currentUser && (
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 500, mb: 1 }}>
          Welcome
          <Box
            component="span"
            sx={(theme) => ({
              fontWeight: 700,
              ml: 1,
              px: 2,
              py: 0.5,
              borderRadius: 2,
              boxShadow: 1,

              letterSpacing: 1,
              fontSize: "1.2em",
            })}
          >
            {currentUser.first_name} {currentUser.last_name}
          </Box>
        </Typography>
      )}
      {/* Header with refresh button */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid item xs>
          <Typography variant="h4" component="h1" gutterBottom>
            Master Investor Dashboard
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="subtitle1" color="text.secondary">
            Last updated: {new Date().toLocaleString()}
          </Typography>
        </Grid>
        <Grid item>
          <IconButton
            onClick={handleRefresh}
            color="primary"
            aria-label="refresh dashboard"
          >
            <RefreshIcon />
          </IconButton>
        </Grid>
      </Grid>

      {/* Risk Alerts - 3 cards in a row */}
      {/* <Grid container spacing={3} sx={{ mb: 3 }}>
        {metrics.alerts.map((alert) => (
          <Grid item xs={12} md={4} key={alert.id}>
            <RiskAlertCard alert={alert} />
          </Grid>
        ))}
      </Grid> */}

      {/* Summary Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={3}
            sx={{
              height: "100%",
              minHeight: "140px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent
              sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Total Portfolio Value
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#ffffff",
                    bgcolor: "primary.main",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontWeight: "medium",
                  }}
                >
                  FY 2025
                </Typography>
              </Box>
              <Typography variant="h4" component="div">
                {formatCurrency(summaryMetrics.totalPortfolioValue || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                From {summaryMetrics.totalInvoices} active invoices
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={3}
            sx={{
              height: "100%",
              minHeight: "140px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent
              sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Active Funders
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#ffffff",
                    bgcolor: "primary.main",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontWeight: "medium",
                  }}
                >
                  FY 2025
                </Typography>
              </Box>
              <Typography variant="h4" component="div">
                {summaryMetrics.activeFunders}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg. rate:{" "}
                {formatPercentage(summaryMetrics.portfolioReturnRate || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={3}
            sx={{
              height: "100%",
              minHeight: "140px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent
              sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Active Invoices
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#ffffff",
                    bgcolor: "primary.main",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontWeight: "medium",
                  }}
                >
                  FY 2025
                </Typography>
              </Box>
              <Typography variant="h4" component="div">
                {summaryMetrics.totalInvoices}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ready for new investments
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={3}
            sx={{
              height: "100%",
              minHeight: "140px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent
              sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Portfolio Return Rate
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#ffffff",
                    bgcolor: "primary.main",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontWeight: "medium",
                  }}
                >
                  FY 2025
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="h4" component="div" sx={{ mr: 1 }}>
                  {formatPercentage(summaryMetrics.portfolioReturnRate || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Annual
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Based on {summaryMetrics.totalInvoices} invoices
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Portfolio Breakdown and Performance */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Investor Allocation */}
        <Grid item xs={12} lg={6}>
          <Card elevation={3} sx={{ height: "100%" }}>
            <CardContent>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                AUM Breakdown by Investor
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={12} lg={6}>
                  <Box
                    sx={{
                      height: 380,
                      maxWidth: 400, // 👈 adjust this value to control chart width
                      width: "80%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      px: 2,
                      mx: "auto", // centers the box horizontally
                    }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={formattedData}
                        margin={{ top: 10, right: 10, bottom: 10, left: 35 }}
                      >
                        <YAxis
                          tickFormatter={(value) => formatCurrency(value)}
                        />
                        <XAxis dataKey="name" />
                        <Tooltip
                          formatter={(value) => formatCurrency(Number(value))}
                        />
                        <Legend />
                        {metrics.topInvestors.map((entry, index) => (
                          <Bar
                            key={entry.id}
                            dataKey={entry.name}
                            stackId="a"
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>
                <Grid item xs={12} md={12} lg={6}>
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}
                  >
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<FileDownloadIcon />}
                      onClick={() => {
                        exportToCsv(
                          metrics.topInvestors.map((investor) => ({
                            Investor: investor.name,
                            Allocation: investor.allocation,
                          })),
                          [],
                          {
                            Investor: { headerName: "Investor" },
                            Allocation: {
                              headerName: "Allocation",
                              valueFormatter: (value: number) =>
                                formatCurrency(value),
                            },
                          }
                        );
                      }}
                    >
                      Export CSV
                    </Button>
                  </Box>
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      // height: 240, // Fixed height for scroll
                      overflowY: "auto",
                      mb: 2,
                      boxShadow: "none", // Remove Paper shadow if you want a flat look
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>Investor</StyledTableCell>
                          <StyledTableCell align="right">AUM</StyledTableCell>
                          <StyledTableCell align="right">
                            Percentage
                          </StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {metrics.topInvestors.map((investor, index) => (
                          <AlternatingRow key={investor.id} index={index}>
                            <TableCell>{investor.name}</TableCell>
                            <TableCell align="right">
                              {formatCurrency(investor.allocation)}
                            </TableCell>
                            <TableCell align="right">
                              {investor?.percentage?.toFixed(2)}%
                            </TableCell>
                          </AlternatingRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Portfolio Performance */}
        <Grid item xs={12} lg={6}>
          <Card elevation={3} sx={{ height: "100%" }}>
            <CardContent sx={{ height: "100%", p: 2 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Portfolio Performance
              </Typography>
              <Box
                sx={{
                  height: "calc(100% - 40px)",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {(() => {
                  // Normalize returns for all years: if value is between 0 and 1, multiply by 100
                  let chartData = metrics.yearlyPerformanceTrend
                    ? [...metrics.yearlyPerformanceTrend]
                    : [];
                  chartData = chartData.map((item) => {
                    let returns = item.returns;
                    if (returns < 1 && returns > 0) {
                      returns *= 100;
                    }
                    return { ...item, returns };
                  });
                  return chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={chartData}
                        margin={{ top: 10, right: 30, left: 30, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient
                            id="colorAum"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor="#8884d8"
                              stopOpacity={0.8}
                            />
                            <stop
                              offset="95%"
                              stopColor="#8884d8"
                              stopOpacity={0.1}
                            />
                          </linearGradient>
                          <linearGradient
                            id="colorReturns"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor="#82ca9d"
                              stopOpacity={0.8}
                            />
                            <stop
                              offset="95%"
                              stopColor="#82ca9d"
                              stopOpacity={0.1}
                            />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eeeeee" />
                        <XAxis dataKey="year" />
                        <YAxis
                          yAxisId="left"
                          orientation="left"
                          width={55}
                          tickFormatter={(value) => {
                            if (value >= 1000000) {
                              return `$${(value / 1000000).toFixed(1)}M`;
                            }
                            return `$${(value / 1000).toFixed(0)}k`;
                          }}
                        />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          domain={[0, 10]}
                          tickFormatter={(value) =>
                            `${Number(value).toFixed(1)}%`
                          }
                          width={40}
                        />
                        <Tooltip
                          formatter={(value, name) => {
                            if (name === "Portfolio Value")
                              return formatCurrency(Number(value));
                            if (name === "Return Rate (%)")
                              return `${Number(value).toFixed(2)}%`;
                            return value;
                          }}
                        />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="aum"
                          name="Portfolio Value"
                          stroke="#8884d8"
                          yAxisId="left"
                          fillOpacity={1}
                          fill="url(#colorAum)"
                        />
                        <Area
                          type="monotone"
                          dataKey="returns"
                          name="Return Rate (%)"
                          stroke="#82ca9d"
                          yAxisId="right"
                          fillOpacity={1}
                          fill="url(#colorReturns)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      align="center"
                    >
                      No data available
                    </Typography>
                  );
                })()}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Transactions */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <ActiveInvoices />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Invoice Chart Modal */}
      <Dialog
        open={invoiceChartOpen}
        onClose={() => setInvoiceChartOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedInvoice ? (
            <>
              Invoice Details:{" "}
              {`${selectedInvoice.id} - ${selectedInvoice.buyerName}`}
            </>
          ) : (
            "Invoice Details"
          )}
        </DialogTitle>
        <DialogContent>
          {selectedInvoice && (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Buyer (Importer)
                  </Typography>
                  <Typography variant="body1">
                    {selectedInvoice.buyerName}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Supplier (Exporter)
                  </Typography>
                  <Typography variant="body1">
                    {selectedInvoice.supplierName}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Principal Amount
                  </Typography>
                  <Typography variant="body1">
                    {formatCurrency(selectedInvoice.principalAmount)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Interest Amount
                  </Typography>
                  <Typography variant="body1">
                    {formatCurrency(selectedInvoice.interestAmount)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Amount
                  </Typography>
                  <Typography variant="body1">
                    {formatCurrency(selectedInvoice.totalAmount)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Sector
                  </Typography>
                  <Typography variant="body1">
                    {selectedInvoice.sector}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Days Financed
                  </Typography>
                  <Typography variant="body1">
                    {selectedInvoice.daysFinanced}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Typography variant="body1">
                    {selectedInvoice.status}
                  </Typography>
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom>
                Interest Accrual Over Time
              </Typography>
              <Box sx={{ width: "100%", height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={generateInvoiceChartData()}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="day"
                      label={{
                        value: "Days",
                        position: "insideBottomRight",
                        offset: -10,
                      }}
                    />
                    <YAxis
                      tickFormatter={(value) =>
                        value.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })
                      }
                    />
                    <Tooltip
                      formatter={(value) =>
                        value.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      }
                      labelFormatter={(label) => `Day ${label}`}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="principal"
                      stackId="1"
                      stroke="#8884d8"
                      fill="#8884d8"
                      name="Principal"
                    />
                    <Area
                      type="monotone"
                      dataKey="interest"
                      stackId="1"
                      stroke="#82ca9d"
                      fill="#82ca9d"
                      name="Interest"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceChartOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialog.open}
        onClose={closeDetailsDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{detailsDialog.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">{detailsDialog.content}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetailsDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MasterInvestorDashboard;
